import React, { useEffect, useState, memo, useRef } from "react";
import { loadGoogleMapsScript } from "../services/GoogleMap";

const GoogleMapArea = memo(({ areaOld = [], onAreaSelected }) => {
  const [mapInstance, setMapInstance] = useState(null);
  const [drawingManager, setDrawingManager] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const eventListeners = useRef([]);
  const isInitialized = useRef(false);

  // Clean up event listeners
  useEffect(() => {
    return () => {
      eventListeners.current.forEach(listener => {
        window.google?.maps?.event?.removeListener(listener);
      });
      eventListeners.current = [];
    };
  }, []);

  const addListener = (target, eventName, callback) => {
    if (!window.google || !window.google.maps) return;
    const listener = window.google.maps.event.addListener(target, eventName, callback);
    eventListeners.current.push(listener);
    return listener;
  };

  const setupPolygonListeners = (polygon) => {
    const path = polygon.getPath();
    
    const listeners = [
      addListener(path, 'insert_at', () => {
        console.log('Vertex added');
        handlePolygonEdit();
      }),
      addListener(path, 'remove_at', () => {
        console.log('Vertex removed');
        handlePolygonEdit();
      }),
      addListener(path, 'set_at', () => {
        console.log('Vertex moved');
        handlePolygonEdit();
      }),
      addListener(polygon, 'rightclick', (e) => {
        if (e.vertex !== undefined) {
          console.log('Right-click on vertex', e.vertex);
          path.removeAt(e.vertex);
          // Explicitly trigger update since removeAt might not trigger remove_at immediately
          handlePolygonEdit();
        }
      })
    ];

    return listeners;
  };

  const handlePolygonEdit = () => {
    if (!mapInstance || !onAreaSelected || !isInitialized.current) return;
    
    console.log('Handling polygon edit');
    
    const allPaths = polygons.map(polygon => {
      try {
        const path = polygon.getPath();
        const array = path.getArray();
        console.log('Current path:', array);
        return array.map(latLng => ({
          lat: latLng.lat(),
          lng: latLng.lng(),
        }));
      } catch (error) {
        console.error('Error getting polygon path:', error);
        return null;
      }
    }).filter(path => path && path.length >= 3);

    console.log('All paths:', allPaths);
    onAreaSelected(allPaths);
  };

  useEffect(() => {
    if (isInitialized.current) {
      handlePolygonEdit();
    }
  }, [polygons]);

  const initializeMap = async () => {
    const map_key = localStorage.getItem('map_key');
    if (!map_key) {
      console.error("Google Maps API key is missing");
      return;
    }

    try {
      const googleMaps = await loadGoogleMapsScript(map_key, [
        "drawing",
        "places",
      ]);

      const map = new googleMaps.Map(document.getElementById("map"), {
        center: { lat: 25.276987, lng: 55.296249 },
        zoom: 13,
      });

      setMapInstance(map);

      const drawingManagerInstance = new googleMaps.drawing.DrawingManager({
        drawingMode: googleMaps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: googleMaps.ControlPosition.TOP_CENTER,
          drawingModes: [googleMaps.drawing.OverlayType.POLYGON],
        },
        polygonOptions: {
          fillColor: "#FF0000",
          fillOpacity: 0.2,
          strokeWeight: 2,
          strokeColor: "#FF0000",
          clickable: true,
          editable: true,
          draggable: true,
        },
      });

      drawingManagerInstance.setMap(map);
      setDrawingManager(drawingManagerInstance);

      // Handle new polygon creation
      addListener(
        drawingManagerInstance,
        "overlaycomplete",
        (event) => {
          if (event.type === "polygon") {
            const newPolygon = event.overlay;
            setPolygons(prev => [...prev, newPolygon]);
            setupPolygonListeners(newPolygon);
            drawingManagerInstance.setDrawingMode(null);
          }
        }
      );

      // Initialize all old areas
      if (areaOld && areaOld.length > 0) {
        const oldPolygons = areaOld.map((oldArea, index) => {
          if (!oldArea || oldArea.length < 3) return null;
          
          const polygon = new googleMaps.Polygon({
            paths: oldArea,
            map,
            editable: true,
            draggable: true,
            strokeColor: "#00AA00",
            fillColor: "#00AA00",
            fillOpacity: 0.2,
            strokeWeight: 2,
            zIndex: index,
          });

          setupPolygonListeners(polygon);
          return polygon;
        }).filter(Boolean);

        setPolygons(oldPolygons);
        
        if (areaOld[0] && areaOld[0][0]) {
          map.setCenter(areaOld[0][0]);
        }
      }

      isInitialized.current = true;
    } catch (error) {
      console.error("Error loading Google Maps:", error);
    }
  };

  const clearAllPolygons = () => {
    polygons.forEach(polygon => polygon.setMap(null));
    setPolygons([]);
  };

  const deletePolygon = (index) => {
    if (index >= 0 && index < polygons.length) {
      const updatedPolygons = [...polygons];
      updatedPolygons[index].setMap(null);
      updatedPolygons.splice(index, 1);
      setPolygons(updatedPolygons);
    }
  };

  useEffect(() => {
    initializeMap();
  }, []);

  // Add this inside your component, before the return statement
const polygonLabels = useRef({});

// Function to create or update labels for all polygons
const updatePolygonLabels = () => {
  if (!mapInstance || !window.google || !window.google.maps) return;

  // First, clear all existing labels
  Object.values(polygonLabels.current).forEach(label => {
    if (label && label.div && label.div.parentNode) {
      label.div.parentNode.removeChild(label.div);
    }
  });
  polygonLabels.current = {};

  // Add labels for each polygon
  polygons.forEach((polygon, index) => {
    try {
      const labelIndex = index + 1;
      const label = new window.google.maps.OverlayView();
      
      label.onAdd = function() {
        const div = document.createElement('div');
        div.className = 'polygon-label';
        div.innerHTML = labelIndex;
        
        // Style the label
        div.style.position = 'absolute';
        div.style.backgroundColor = 'white';
        div.style.borderRadius = '50%';
        div.style.width = '24px';
        div.style.height = '24px';
        div.style.textAlign = 'center';
        div.style.lineHeight = '24px';
        div.style.fontWeight = 'bold';
        div.style.border = '2px solid #333';
        div.style.zIndex = '1000';
        
        this.div = div;
        const panes = this.getPanes();
        panes.floatPane.appendChild(div);
      };
      
      label.onRemove = function() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
      };
      
      label.draw = function() {
        if (!this.div) return;
        
        // Position the label at the centroid of the polygon
        const bounds = new window.google.maps.LatLngBounds();
        const paths = polygon.getPaths();
        
        paths.forEach(path => {
          path.getArray().forEach(latLng => {
            bounds.extend(latLng);
          });
        });
        
        const center = bounds.getCenter();
        const projection = this.getProjection();
        const position = projection.fromLatLngToDivPixel(center);
        
        if (position) {
          this.div.style.left = (position.x - 12) + 'px';
          this.div.style.top = (position.y - 12) + 'px';
        }
      };
      
      label.setMap(mapInstance);
      polygonLabels.current[index] = label;
    } catch (error) {
      console.error('Error creating polygon label:', error);
    }
  });
};

// Call this whenever polygons change
useEffect(() => {
  if (isInitialized.current) {
    updatePolygonLabels();
  }
}, [polygons, mapInstance]);

  return (
    <div>
      <div id="map" style={{ height: "400px", width: "100%" }} />
      <div style={{ marginTop: "10px" }}>
        {polygons.map((_, index) => (
          <button  type="button" className="btn btn-secondary btn-sm"
            key={index}
            onClick={() => deletePolygon(index)}
            style={{ marginRight: "10px" }}
          >
            Delete Area {index + 1}
          </button>
        ))}
      </div>
    </div>
  );
});

export default GoogleMapArea;